import { useForm, yupResolver } from "@mantine/form"
import { useLocalStorage } from "@mantine/hooks"
import { CheckCircle, Info, Plus } from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Card,
  FormGroup,
  Group,
  MenuDivider,
  QuerySelector,
  SpinnerOverlay,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { DateInput, FormDateInput } from "@vesatogo/grass-dates"
import "@vesatogo/grass-dates/index.css"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { cloneDeep, debounce, set } from "lodash-es"
import { memo, useEffect, useMemo, useState } from "react"
import { toast } from "react-hot-toast"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useAuthorization } from "~/components/AuthorizationProvider"
import CreateReceiptSummary from "~/components/CreateReceiptSummary"
import { FormCard } from "~/components/FormCard"
import HowIsItTraded from "~/components/HowIsItTraded"
import { PackingMaterialSaveModal } from "~/components/PackingMaterial/PackingMaterialSaveModal"
import TermsAndConditions from "~/components/TermsAndConditions"
import { ReceiptParticulars } from "~/components/Trade/ReceiptParticulars"
import TradeExpenses, { EXPENSE } from "~/components/TradeExpenses"
import WhatIsBeingTraded from "~/components/WhatIsBeingTraded"
import { WhoIsTrading } from "~/components/WhoIsTrading"
import WhoOverseesTrading from "~/components/WhoOverseesTrading"
import Codenames from "~/constants/Codenames"
import { TradeKindsReadable, canAddAdvancePayment } from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { AppRoutes } from "~/constants/routes"
import {
  ReceiptDetailQuery,
  useAddUpdateReceiptMutation,
  useAddUpdateSharedEntityMutation,
  useAllDepartmentsQuery,
  useAllVehiclesQuery,
  useGetSharedEntityQuery,
  useOrganisationPricingQuery,
  useReceiptDetailQuery,
  useUserPackingQuery,
} from "~/generated/graphql"
import { receiptValidationSchema } from "~/models/receipts"
import {
  ReceiptState,
  Trade,
  TradeItem,
} from "~/pages/d/trade-requests/receipts/types"
import { SideBarItem } from "~/pages/d/trade-requests/sections/SideBar"
import { expenseAmount, normalizeExpenses } from "~/utils/expenseAmount"
import {
  buildReceiptTradeItemInputsFromBlocks,
  createEmptyReceiptCommodityBlock,
  syncReceiptFormValues,
  type ReceiptCommodityBlock,
} from "~/utils/receiptParticularsItems"
import { useDepartment } from "~/utils/useDepartment"

type SharedEntity = {
  group: SharedEntityGroup
}

export const TRADE_ITEM: Partial<TradeItem> = {
  grade: null,
}
const TRADE: Trade = {
  buyer: null,
  attachments: [] as any,
  items: [TRADE_ITEM],
  commodity_items: [createEmptyReceiptCommodityBlock()],
}

const INITIAL_STATE: ReceiptState = {
  mode: "direct",
  kind: "pro_forma",
  packaging: "raw_material",
  trades: [TRADE],
  child_receipts: [],
  start_date: new Date(),
  end_date: null,
  expiry: null,
  expenses: [EXPENSE],
  total_quantity: 0,
  total_amount: 0,
  seller_expense_amount: 0,
  seller_receivable_amount: 0,
  advance_payment: [],
  credit_notes: [],
  advance_amount: 0,
  supplierAdvanceAmount: 0,
  supplier_gst_auto_calculated: true,
  expense_supplier_gst_auto_calculated: true,
  expense_supplier_sgst: 0,
  expense_supplier_cgst: 0,
  expense_supplier_igst: 0,
  expense_supplier_sgst_percentage: 0,
  expense_supplier_cgst_percentage: 0,
  expense_supplier_igst_percentage: 0,
  expense_seller_gst_attachments: [],
  supplier_charge: 0,
  allow_multiple_commodity: false,
}

type PaymentRowProps = {
  label: React.ReactNode
  value: React.ReactNode
  spacer?: boolean
}

const PaymentRow = memo(({ label, value, spacer = true }: PaymentRowProps) => (
  <div className="flex justify-between items-center py-2 px-4">
    <span className="text-gray-700 text-sm">{label}</span>
    <div className="flex items-center gap-4">
      {value}
      {spacer && <div className="w-[40px]" />}
    </div>
  </div>
))

const ReceiptDetail = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [, upsertSharedEntity] = useAddUpdateSharedEntityMutation()
  const [{ fetching: mutationFetching }, addUpdateReceipt] =
    useAddUpdateReceiptMutation()
  const { id: sharedDeptId, department: departmentData } = useDepartment()
  const [dept] = useLocalStorage({ key: "department" })

  const [{ data: alldepts }] = useAllDepartmentsQuery()

  const currentDept = alldepts?.department?.find(
    dep => dep?.id?.toString() === dept?.toString()
  )

  const [{ data: deptData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteDepartment,
      codename: currentDept?.id,
    },
    pause: !currentDept?.id,
  })

  const useCredit =
    deptData?.setu_trade_shared_entity?.[0]?.department_config?.use_credit

  const [{ data: sData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteCurrency,
      codename: Codenames.KiteDefaultCurrency,
    },
  })

  const defaultCurrency = sData?.setu_trade_shared_entity?.[0]
  const [{ data: oData }] = useOrganisationPricingQuery()
  const { id } = useParams()

  const form = useForm<ReceiptState>({
    initialValues: INITIAL_STATE,
    validate: yupResolver(receiptValidationSchema),
  })
  const { values: state, setValues: setState } = form

  const [isOpen, setOpen] = useState(false)
  const [packingOpen, setPackingOpen] = useState(false)

  const totalSupplierGst =
    (form.values.supplier_sgst || 0) +
    (form.values.supplier_cgst || 0) +
    (form.values.supplier_igst || 0)
  const totalExpenseSupplierGst =
    (form.values.expense_supplier_igst || 0) +
    (form.values.expense_supplier_sgst || 0) +
    (form.values.expense_supplier_cgst || 0)
  const supplierSameState =
    form.values.seller?.current_address?.state_region?.code &&
    departmentData?.address?.state_region?.code
      ? form.values.seller?.current_address?.state_region?.code ===
        departmentData?.address?.state_region?.code
      : undefined

  const idNum = parseInt(id as any)
  const newfprId = parseInt(searchParams.get("newfpr") || "")

  const [{ data: dData, fetching }] = useReceiptDetailQuery({
    variables: { id: idNum },
    pause: !idNum,
    requestPolicy: "network-only",
  })
  const [{ data: pData }] = useUserPackingQuery({
    variables: {
      where: {
        user_id: {
          _eq: form.values?.seller?.shared_id,
        },
      },
    },
  })

  const isDisabled =
    (dData?.receipt_by_pk?.editable_trades?.aggregate?.count &&
      form.values?.id) ||
    dData?.receipt_by_pk?.child_receipts_aggregate?.aggregate?.count
      ? true
      : false

  const canAddMultiCommodity = useAuthorization(
    Permission.CanAddMultiCommodityTradesAndReceipts
  )

  const isHishobPattiAdded =
    dData?.receipt_by_pk?.hishob_patti_count?.aggregate?.count &&
    dData?.receipt_by_pk?.kind === "booking" &&
    form.values?.id &&
    dData?.receipt_by_pk?.child_receipts_aggregate?.aggregate?.count
      ? true
      : false
  function updateReceiptState(receipt?: ReceiptDetailQuery["receipt_by_pk"]) {
    if (!receipt) return

    const tradeItemTaxes =
      receipt.trades
        ?.flatMap(trade => trade.trade_items)
        ?.flatMap(item => item?.taxes ?? []) ?? []

    const supplier_sgst =
      tradeItemTaxes
        ?.filter((t: any) => t.tax_name === "SGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const supplier_cgst =
      tradeItemTaxes
        ?.filter((t: any) => t.tax_name === "CGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const supplier_igst =
      tradeItemTaxes
        ?.filter((t: any) => t.tax_name === "IGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const supplier_sgst_percentage =
      tradeItemTaxes?.find(
        (t: any) => t.tax_name === "SGST" && t.role === "seller"
      )?.percentage || 0
    const supplier_cgst_percentage =
      tradeItemTaxes?.find(
        (t: any) => t.tax_name === "CGST" && t.role === "seller"
      )?.percentage || 0
    const supplier_igst_percentage =
      tradeItemTaxes?.find(
        (t: any) => t.tax_name === "IGST" && t.role === "seller"
      )?.percentage || 0

    const _gstFromBackend = (tradeItemTaxes?.length ?? 0) > 0
    const _gstExpenseFromBackend = (dData?.trade_expense_tax?.length ?? 0) > 0

    const expense_supplier_sgst =
      dData?.trade_expense_tax
        ?.filter((t: any) => t.tax_name === "SGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const expense_supplier_cgst =
      dData?.trade_expense_tax
        ?.filter((t: any) => t.tax_name === "CGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const expense_supplier_igst =
      dData?.trade_expense_tax
        ?.filter((t: any) => t.tax_name === "IGST" && t.role === "seller")
        .reduce((sum: number, t: any) => sum + t.amount, 0) || 0
    const expense_supplier_sgst_percentage =
      dData?.trade_expense_tax?.find(
        (t: any) => t.tax_name === "SGST" && t.role === "seller"
      )?.percentage || 0
    const expense_supplier_cgst_percentage =
      dData?.trade_expense_tax?.find(
        (t: any) => t.tax_name === "CGST" && t.role === "seller"
      )?.percentage || 0
    const expense_supplier_igst_percentage =
      dData?.trade_expense_tax?.find(
        (t: any) => t.tax_name === "IGST" && t.role === "seller"
      )?.percentage || 0

    const isMultipleCommodity = Boolean(receipt.is_multiple_commodity)
    const firstItem = receipt.trades?.[0]?.trade_items?.[0]
    const firstCommodity = firstItem?.commodity?.commodity?.[0]
      ? {
          ...firstItem.commodity.commodity[0],
          shared_id: firstItem.commodity.id,
        }
      : undefined
    const firstVariety = firstItem?.commodity_variety?.commodity_variety?.[0]
      ? {
          ...firstItem.commodity_variety.commodity_variety[0],
          shared_id: firstItem.commodity_variety.id,
        }
      : undefined
    const firstPlotCrop = firstItem?.plot_crop?.plot_crop?.[0]
      ? {
          ...firstItem.plot_crop.plot_crop[0],
          shared_id: firstItem.plot_crop.id,
        }
      : undefined
    const firstUnit = firstItem?.quantity_unit
      ? {
          ...firstItem.quantity_unit,
          shared_id: firstItem.quantity_unit.id,
        }
      : undefined

    const newState = {
      ...receipt,
      supplier_sgst_percentage,
      supplier_cgst_percentage,
      supplier_igst_percentage,
      supplier_gst_auto_calculated:
        (receipt as any).supplier_gst_auto_calculated ?? true,
      id: newfprId ? undefined : receipt.id,
      buyer_tnc: receipt.buyer_tnc,
      seller_tnc: receipt.seller_tnc,
      kind: Boolean(searchParams.get("new")) ? "final" : receipt.kind,
      mode: receipt.mode,
      commodity: isMultipleCommodity ? undefined : firstCommodity,
      commodity_variety: isMultipleCommodity ? undefined : firstVariety,
      vehicle: receipt.vehicle
        ? {
            ...receipt.vehicle,
            shared_id: receipt?.vehicle?.id,
          }
        : undefined,
      plot_crop: isMultipleCommodity ? undefined : firstPlotCrop,
      unit: isMultipleCommodity ? undefined : firstUnit,
      facilitator: {
        ...receipt.facilitator?.facilitator[0],
        shared_id: receipt.facilitator?.id,
      },
      seller: {
        ...receipt?.seller?.user[0],
        shared_id: receipt?.seller?.id,
      },

      total_amount: receipt.total_amount,
      total_quantity: receipt.total_quantity,
      amount_deducted:
        (receipt.total_amount * receipt.standard_deduction) / 100,
      quantity_deducted:
        (receipt.total_quantity * receipt.standard_deduction) / 100,
      packaging: receipt.packaging as any,
      seller_receivable_amount: receipt.seller_receivable_amount || 0,
      seller_expense_amount: receipt.seller_expense_amount || 0,
      allow_multiple_commodity: isMultipleCommodity,
      supplier_sgst,
      supplier_cgst,
      supplier_igst,
      _gstFromBackend,
      _gstExpenseFromBackend,
      expense_supplier_sgst,
      expense_supplier_cgst,
      expense_supplier_igst,
      expense_supplier_sgst_percentage,
      expense_supplier_cgst_percentage,
      expense_supplier_igst_percentage,
      expense_supplier_gst_auto_calculated:
        (receipt as any)?.supplier_expense_gst_auto_calculated ?? true,
      standard_deduction: receipt.standard_deduction,
      start_date: receipt.start_date && new Date(receipt.start_date),
      end_date: receipt.end_date && new Date(receipt.end_date),
      expiry: receipt.expiry && new Date(receipt.expiry),
      parent_receipt_id: newfprId ? newfprId : receipt?.parent_receipt_id,
      seller_gst_attachments: (receipt as any).seller_gst_attachments || [],
      officer: receipt.officer?.user[0]
        ? { ...receipt.officer?.user[0], shared_id: receipt.officer?.id }
        : undefined,
      junior_officer: receipt.junior_officer?.user[0]
        ? {
            ...receipt.junior_officer?.user[0],
            shared_id: receipt.junior_officer?.id,
          }
        : undefined,
      trades: receipt.trades?.length
        ? receipt.trades.map(trade => {
            const rawItems = trade.trade_items?.length ? trade.trade_items : []
            const mappedItems = rawItems.length
              ? rawItems.map(item => ({
                  id: newfprId ? undefined : item.id,
                  quantity: item.quantity,
                  rate: item.rate,
                  total_amount: item.total_amount,
                  parent_trade_item_id: newfprId
                    ? item.id
                    : item.parent_trade_item_id,
                  grade: {
                    ...item.variety_grade?.variety_grade[0],
                    shared_id: item.variety_grade?.id,
                  },
                }))
              : [TRADE_ITEM]

            const groupedItems = new Map<string, any[]>()
            rawItems.forEach(item => {
              const commodityId = item.commodity?.id ?? firstCommodity?.id
              const varietyId = item.commodity_variety?.id ?? firstVariety?.id
              const plotCropId = item.plot_crop?.id ?? firstPlotCrop?.id
              const key = `${commodityId}|${varietyId}|${plotCropId}`
              if (!groupedItems.has(key)) groupedItems.set(key, [])
              groupedItems.get(key)!.push(item)
            })

            const buildBlockGst = (groupItems: any[]) => {
              const groupTaxes = groupItems.flatMap(
                (item: any) => item?.taxes ?? []
              )
              const sellerTax = (name: string) =>
                groupTaxes
                  .filter(
                    (t: any) => t.tax_name === name && t.role === "seller"
                  )
                  .reduce((sum: number, t: any) => sum + t.amount, 0)
              const sellerTaxPct = (name: string) =>
                groupTaxes.find(
                  (t: any) => t.tax_name === name && t.role === "seller"
                )?.percentage || 0
              return {
                supplier_sgst: sellerTax("SGST"),
                supplier_cgst: sellerTax("CGST"),
                supplier_igst: sellerTax("IGST"),
                supplier_sgst_percentage: sellerTaxPct("SGST"),
                supplier_cgst_percentage: sellerTaxPct("CGST"),
                supplier_igst_percentage: sellerTaxPct("IGST"),
                supplier_gst_auto_calculated:
                  (receipt as any).supplier_gst_auto_calculated ?? true,
                _gstFromBackend: groupTaxes.length > 0,
                seller_gst_attachments:
                  (receipt as any).seller_gst_attachments || [],
              }
            }

            const buildBlock = (groupItems: any[]): ReceiptCommodityBlock => {
              const firstItem = groupItems[0]
              return {
                ...buildBlockGst(groupItems),
                commodity: firstItem?.commodity?.commodity?.[0]
                  ? {
                      ...firstItem.commodity.commodity[0],
                      shared_id: firstItem.commodity.id,
                    }
                  : firstCommodity,
                commodity_variety: firstItem?.commodity_variety
                  ?.commodity_variety?.[0]
                  ? {
                      ...firstItem.commodity_variety.commodity_variety[0],
                      shared_id: firstItem.commodity_variety.id,
                    }
                  : firstVariety,
                plot_crop: firstItem?.plot_crop?.plot_crop?.[0]
                  ? {
                      ...firstItem.plot_crop.plot_crop[0],
                      shared_id: firstItem.plot_crop.id,
                    }
                  : firstPlotCrop,
                quantity_unit: (() => {
                  const siUnit = firstItem?.quantity_unit?.si_unit?.[0]
                  return siUnit
                    ? {
                        ...siUnit,
                        shared_id: firstItem.quantity_unit?.id,
                      }
                    : undefined
                })(),
                grades: groupItems.map(item => ({
                  grade: {
                    ...item.variety_grade?.variety_grade[0],
                    shared_id: item.variety_grade?.id,
                  },
                  rate: item.rate,
                  quantity: item.quantity,
                  id: newfprId ? undefined : item.id,
                  parent_trade_item_id: newfprId
                    ? item.id
                    : item.parent_trade_item_id,
                })),
              }
            }

            const commodityItems = rawItems.length
              ? Array.from(groupedItems.values()).map(buildBlock)
              : [
                  {
                    commodity: firstCommodity,
                    commodity_variety: firstVariety,
                    plot_crop: firstPlotCrop,
                    quantity_unit: undefined,
                    grades: [{ grade: null, rate: 0, quantity: 0 }],
                  } as ReceiptCommodityBlock,
                ]

            return {
              id: newfprId ? undefined : trade.id,
              attachments: trade.attachments,
              buyer: {
                ...trade.buyer?.user[0],
                shared_id: trade.buyer?.id,
              },
              total_amount: trade.total_amount,
              total_quantity: trade.total_quantity,
              items: mappedItems,
              commodity_items: commodityItems,
            }
          })
        : [TRADE],

      expenses: normalizeExpenses(receipt.expenses, newfprId ? true : false),
      advance_payment: newfprId
        ? []
        : receipt.advance_payments?.map(advance => ({
            ...advance,
            bourn_by: { id: advance.bourn_by, name: advance.bourn_by },
          })),
      credit_notes: receipt?.credit_notes,
      expense_amount: receipt.expense_amount,
    }

    setState(newState)
    runComputations(newState)
  }
  const receiptId = dData?.receipt_by_pk?.id

  useEffect(() => {
    if (receiptId) {
      updateReceiptState(dData?.receipt_by_pk)
    }
  }, [receiptId, searchParams.get("new")])

  useEffect(() => {
    if (
      dData &&
      sharedDeptId &&
      dData?.receipt_by_pk?.department.id !== sharedDeptId
    ) {
      navigate(AppRoutes.tradeReceipts)
    }
  }, [sharedDeptId, dData?.receipt_by_pk?.department.reference_id])

  const settledByAdvance = dData?.receipt_by_pk?.settled_by_advance_payment
  const settledByAdvanceAmount = useMemo(
    () =>
      (settledByAdvance || []).reduce(
        (total, transaction) => total + (Number(transaction.amount) || 0),
        0
      ),
    [settledByAdvance]
  )

  const runComputations = useMemo(
    () =>
      debounce(values => {
        const stateCopy = cloneDeep(values)
        let receiptTotalQty = 0,
          receiptTotalAmount = 0,
          receiptTotalKg = 0,
          totalExpenseAmount = 0,
          sellerExpenseAmount = 0,
          advanceTotalAmount = 0

        const synced = syncReceiptFormValues(stateCopy)

        for (let i = 0; i < synced.trades.length; i++) {
          const trade = synced.trades[i]
          let tradeTotalQty = 0,
            tradeTotalAmount = 0,
            tradeTotalKg = 0

          if (trade.commodity_items?.length) {
            for (let c = 0; c < trade.commodity_items.length; c++) {
              const block = trade.commodity_items[c]
              const grades = block.grades?.length
                ? block.grades
                : [{ grade: null, rate: 0, quantity: 0 }]
              let blockTotalQty = 0
              for (let j = 0; j < grades.length; j++) {
                const grade = grades[j]
                let itemTotalAmount = 0
                tradeTotalQty += Number(grade.quantity) || 0
                blockTotalQty += Number(grade.quantity) || 0
                itemTotalAmount +=
                  (Number(grade.quantity) || 0) * (Number(grade.rate) || 0)
                tradeTotalAmount += itemTotalAmount
              }
              tradeTotalKg +=
                blockTotalQty *
                (Number(
                  block.quantity_unit?.base_conversion ??
                    block.quantity_unit?.si_unit?.[0]?.base_conversion
                ) || 0)
            }
          } else {
            for (let j = 0; j < trade.items.length; j++) {
              const item = trade.items[j]
              let itemTotalAmount = 0
              tradeTotalQty += Number(item.quantity) || 0
              tradeTotalKg +=
                (Number(item.quantity) || 0) *
                (Number(
                  (item as any).quantity_unit?.base_conversion ??
                    (item as any).quantity_unit?.si_unit?.[0]
                      ?.base_conversion ??
                    synced.unit?.base_conversion
                ) || 0)
              itemTotalAmount +=
                (Number(item.quantity) || 0) * (Number(item.rate) || 0)
              trade.items[j].total_amount = itemTotalAmount
              tradeTotalAmount += itemTotalAmount
            }
          }

          synced.trades[i].total_quantity = tradeTotalQty
          synced.trades[i].total_amount = tradeTotalAmount
          synced.trades[i].total_quantity_kg = tradeTotalKg
          receiptTotalQty += tradeTotalQty
          receiptTotalKg += tradeTotalKg
          receiptTotalAmount += tradeTotalAmount
        }

        synced.total_quantity = receiptTotalQty
        synced.total_amount = receiptTotalAmount
        synced.total_quantity_kg = receiptTotalKg
        synced.amount_deducted =
          synced.total_amount * (Number(synced.standard_deduction || 0) / 100)

        synced.quantity_deducted =
          synced.total_quantity * (Number(synced.standard_deduction || 0) / 100)

        const totalKg =
          receiptTotalKg * (1 - Number(synced.standard_deduction || 0) / 100)
        const supplier_rate = oData?.organisation_pricing?.supplier_rate ?? 0
        const tax_percentage = oData?.organisation_pricing?.tax_percentage ?? 0
        synced.supplier_charge =
          totalKg * supplier_rate +
          (totalKg * supplier_rate * tax_percentage) / 100
        for (let j = 0; j < synced.expenses.length; j++) {
          const expense = synced.expenses[j]
          const expenseParams = {
            kind: expense?.kind?.id,
            buyerAmount: Number(expense.buyer?.value) || 0,
            sellerAmount: Number(expense.seller?.value) || 0,
            facilitatorAmount: Number(expense.facilitator?.value) || 0,
            finalAmount: synced.total_amount,
            finalQuantity: synced.total_quantity - synced.quantity_deducted,
            totalQuantity: synced.total_quantity,
            trades: synced.trades?.length,
          }
          expense.amount = expenseAmount(expenseParams)
          ;(sellerExpenseAmount += expenseAmount({
            ...expenseParams,
            buyerAmount: 0,
          })),
            (totalExpenseAmount += expense.amount)
        }
        for (let j = 0; j < synced.advance_payment.length; j++) {
          const advance = synced.advance_payment[j]
          advanceTotalAmount += Number(advance.amount) || 0
        }

        synced.supplierAdvanceAmount = settledByAdvanceAmount
        synced.advance_amount = advanceTotalAmount
        synced.expense_amount = totalExpenseAmount
        synced.seller_expense_amount = sellerExpenseAmount

        // Per-commodity Supplier GST Calculation
        const deptCode = departmentData?.address?.state_region?.code
        const sellerCode = synced.seller?.current_address?.state_region?.code

        for (let i = 0; i < synced.trades.length; i++) {
          const trade = synced.trades[i]
          if (!trade.commodity_items?.length) continue

          for (let c = 0; c < trade.commodity_items.length; c++) {
            const block = trade.commodity_items[c]
            const isFromBackend = block._gstFromBackend
            if (isFromBackend) {
              block._gstFromBackend = false
              continue
            }

            const blockTotals = (() => {
              const grades = block.grades?.length
                ? block.grades
                : [{ grade: null, rate: 0, quantity: 0 }]
              return grades.reduce(
                (acc, g) => ({
                  quantity: acc.quantity + (Number(g.quantity) || 0),
                  total_cost:
                    acc.total_cost +
                    (Number(g.quantity) || 0) * (Number(g.rate) || 0),
                }),
                { quantity: 0, total_cost: 0 }
              )
            })()

            const blockGstBase =
              blockTotals.total_cost *
              (1 - Number(synced.standard_deduction || 0) / 100)

            if (block.supplier_gst_auto_calculated !== false) {
              const taxesConfig =
                block.commodity_variety
                  ?.organisation_commodity_variety_configs?.[0]?.taxes || []
              if (taxesConfig.length > 0 && deptCode && sellerCode) {
                const parseTax = (name: string) => {
                  const tax = taxesConfig.find((t: any) => t.tax?.name === name)
                  if (!tax) return { amount: 0, percentage: 0 }
                  return {
                    amount: blockGstBase * (tax.tax.percentage / 100),
                    percentage: tax.tax.percentage,
                  }
                }
                if (deptCode === sellerCode) {
                  const sgst = parseTax("SGST")
                  block.supplier_sgst = sgst.amount
                  block.supplier_sgst_percentage = sgst.percentage
                  const cgst = parseTax("CGST")
                  block.supplier_cgst = cgst.amount
                  block.supplier_cgst_percentage = cgst.percentage
                  block.supplier_igst = 0
                  block.supplier_igst_percentage = 0
                } else {
                  const igst = parseTax("IGST")
                  block.supplier_igst = igst.amount
                  block.supplier_igst_percentage = igst.percentage
                  block.supplier_sgst = 0
                  block.supplier_sgst_percentage = 0
                  block.supplier_cgst = 0
                  block.supplier_cgst_percentage = 0
                }
              } else {
                block.supplier_sgst = 0
                block.supplier_cgst = 0
                block.supplier_igst = 0
                block.supplier_sgst_percentage = 0
                block.supplier_cgst_percentage = 0
                block.supplier_igst_percentage = 0
              }
            } else {
              block.supplier_sgst =
                blockGstBase * ((block.supplier_sgst_percentage || 0) / 100)
              block.supplier_cgst =
                blockGstBase * ((block.supplier_cgst_percentage || 0) / 100)
              block.supplier_igst =
                blockGstBase * ((block.supplier_igst_percentage || 0) / 100)
            }
          }
        }

        // Aggregate per-commodity GST to top-level
        let aggSupplierSgst = 0,
          aggSupplierCgst = 0,
          aggSupplierIgst = 0
        let aggSgstPct = 0,
          aggCgstPct = 0,
          aggIgstPct = 0
        let aggAutoCalc = true
        let aggAttachments: any[] = []
        for (const trade of synced.trades) {
          if (!trade.commodity_items?.length) continue
          for (const block of trade.commodity_items) {
            aggSupplierSgst += block.supplier_sgst || 0
            aggSupplierCgst += block.supplier_cgst || 0
            aggSupplierIgst += block.supplier_igst || 0
            aggSgstPct += block.supplier_sgst_percentage || 0
            aggCgstPct += block.supplier_cgst_percentage || 0
            aggIgstPct += block.supplier_igst_percentage || 0
            if (block.supplier_gst_auto_calculated === false)
              aggAutoCalc = false
            if (block.seller_gst_attachments?.length) {
              aggAttachments = [
                ...aggAttachments,
                ...block.seller_gst_attachments,
              ]
            }
          }
        }
        synced.supplier_sgst = aggSupplierSgst
        synced.supplier_cgst = aggSupplierCgst
        synced.supplier_igst = aggSupplierIgst
        synced.supplier_sgst_percentage = aggSgstPct
        synced.supplier_cgst_percentage = aggCgstPct
        synced.supplier_igst_percentage = aggIgstPct
        synced.supplier_gst_auto_calculated = aggAutoCalc
        synced.seller_gst_attachments = aggAttachments

        // Expense GST calculation
        if (!synced._gstExpenseFromBackend) {
          if (synced.expense_supplier_gst_auto_calculated) {
            const taxesConfig =
              synced.commodity_variety
                ?.organisation_commodity_variety_configs?.[0]?.taxes || []
            if (taxesConfig.length > 0) {
              const expenseSupplierAmountForGst =
                synced.seller_expense_amount || 0
              const deptCode = departmentData?.address?.state_region?.code
              const sellerCode =
                synced.seller?.current_address?.state_region?.code

              if (deptCode && sellerCode) {
                const parseTax = (name: string) => {
                  const tax = taxesConfig.find((t: any) => t.tax?.name === name)
                  if (!tax) return { amount: 0, percentage: 0 }
                  return {
                    amount:
                      expenseSupplierAmountForGst * (tax.tax.percentage / 100),
                    percentage: tax.tax.percentage,
                  }
                }
                if (deptCode === sellerCode) {
                  const sgst = parseTax("SGST")
                  synced.expense_supplier_sgst = sgst.amount
                  synced.expense_supplier_sgst_percentage = sgst.percentage
                  const cgst = parseTax("CGST")
                  synced.expense_supplier_cgst = cgst.amount
                  synced.expense_supplier_cgst_percentage = cgst.percentage
                  synced.expense_supplier_igst = 0
                  synced.expense_supplier_igst_percentage = 0
                } else {
                  const igst = parseTax("IGST")
                  synced.expense_supplier_igst = igst.amount
                  synced.expense_supplier_igst_percentage = igst.percentage
                  synced.expense_supplier_sgst = 0
                  synced.expense_supplier_sgst_percentage = 0
                  synced.expense_supplier_cgst = 0
                  synced.expense_supplier_cgst_percentage = 0
                }
              } else {
                if (sellerCode === undefined) {
                  toast.custom(
                    <Toast
                      title="Seller state code missing"
                      subtitle="GST cannot be calculated"
                      intent="warning"
                    />,
                    { id: "receipt_seller_gst_missing" }
                  )
                }
                synced.expense_supplier_sgst = 0
                synced.expense_supplier_cgst = 0
                synced.expense_supplier_igst = 0
                synced.expense_supplier_sgst_percentage = 0
                synced.expense_supplier_cgst_percentage = 0
                synced.expense_supplier_igst_percentage = 0
              }
            } else {
              synced.expense_supplier_sgst = 0
              synced.expense_supplier_cgst = 0
              synced.expense_supplier_igst = 0
              synced.expense_supplier_sgst_percentage = 0
              synced.expense_supplier_cgst_percentage = 0
              synced.expense_supplier_igst_percentage = 0
            }
          } else {
            const expenseSupplierAmountForGst =
              synced.seller_expense_amount || 0

            synced.expense_supplier_sgst =
              expenseSupplierAmountForGst *
              ((synced.expense_supplier_sgst_percentage || 0) / 100)
            synced.expense_supplier_cgst =
              expenseSupplierAmountForGst *
              ((synced.expense_supplier_cgst_percentage || 0) / 100)
            synced.expense_supplier_igst =
              expenseSupplierAmountForGst *
              ((synced.expense_supplier_igst_percentage || 0) / 100)
          }
        }

        const totalGst =
          (synced.supplier_sgst || 0) +
          (synced.supplier_cgst || 0) +
          (synced.supplier_igst || 0)

        const totalExpenseSupplierGst =
          (synced.expense_supplier_igst || 0) +
          (synced.expense_supplier_sgst || 0) +
          (synced.expense_supplier_cgst || 0)

        synced.seller_receivable_amount =
          (synced.total_amount || 0) -
          (synced.amount_deducted || 0) -
          (synced.supplier_charge || 0) -
          (settledByAdvanceAmount || 0) -
          (synced.seller_expense_amount || 0) -
          totalExpenseSupplierGst +
          (parseFloat(synced?.credit_notes?.[0]?.amount) || 0) +
          totalGst
        form.setValues(synced)
      }, 500),
    [departmentData, oData, settledByAdvanceAmount]
  )

  function inputProps(name: string, sharedEntity?: SharedEntity) {
    return {
      ...form.getInputProps(name),
      error: form.errors[name] as any,
      disabled: isDisabled || isHishobPattiAdded,
      onChange: async (e: any) => {
        const stateCopy = cloneDeep(form.values)
        let facilitator = stateCopy.seller?.companies?.[0]?.company
        if (name === "mode" && e === "facilitator" && facilitator) {
          const { data: facilitatorShared } = await upsertSharedEntity({
            reference_id: facilitator.codename,
            group_name: SharedEntityGroup.KiteCompany,
            name: facilitator.name,
          })
          let shared_id =
            facilitatorShared?.insert_setu_trade_shared_entity_one?.id
          set(stateCopy, "facilitator", { ...facilitator, shared_id })
        } else if (
          (name === "mode" && e === "direct") ||
          (name === "mode" && e === "facilitator" && !facilitator) ||
          (name === "seller" && !e?.companies.length)
        ) {
          set(stateCopy, "facilitator", null)
          set(stateCopy, "mode", "direct")
        }

        if (sharedEntity && e?.codename && e?.name && !e?.shared_id) {
          const { data } = await upsertSharedEntity({
            reference_id: e.codename,
            group_name: sharedEntity.group,
            name: e.name,
          })
          set(stateCopy, name, {
            ...e,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
          })
        } else {
          set(stateCopy, name, e?.target ? e.target.value : e)
        }
        runComputations(stateCopy)
        form.setValues(stateCopy)
      },
    }
  }

  function handleSubmit() {
    return form.onSubmit(async values => {
      try {
        const hasOrgConfig =
          (form.values.commodity_variety
            ?.organisation_commodity_variety_configs?.[0]?.taxes?.length ?? 0) >
          0
        const missingBuyerState = form.values.trades?.some(
          trade =>
            trade.buyer?.id && !trade.buyer?.current_address?.state_region
        )

        if (
          hasOrgConfig &&
          (!form.values.seller?.current_address?.state_region ||
            missingBuyerState)
        ) {
          return toast.custom(
            <Toast
              title="Missing State Code"
              intent="danger"
              subtitle="Supplier or one of the buyers is missing a state region code. GST cannot be calculated."
            />
          )
        }

        if (!currentDept?.id) {
          return toast.custom(
            <Toast title="Please select a department" intent="danger" />
          )
        }

        const { data: deptData } = await upsertSharedEntity({
          group_name: SharedEntityGroup.KiteDepartment,
          name: currentDept?.name,
          reference_id: currentDept?.id,
        })

        const { data, error } = await addUpdateReceipt({
          input: {
            id: values.id as any,
            parent_receipt_id: values.parent_receipt_id as any,
            seller_id: values.seller?.shared_id,
            department_id: deptData?.insert_setu_trade_shared_entity_one?.id,
            kind: values.kind,
            mode: values.mode,
            is_multiple_commodity: Boolean(values.allow_multiple_commodity),
            junior_officer_id: values?.junior_officer?.shared_id
              ? values?.junior_officer?.shared_id
              : null,
            officer_id: values?.officer?.shared_id
              ? values?.officer?.shared_id
              : null,
            vehicle_id: values.vehicle?.shared_id,
            buyer_tnc: values.buyer_tnc,
            seller_tnc: values.seller_tnc,
            facilitator_id: values.facilitator?.shared_id,
            packaging: values.packaging,
            start_date: values.start_date
              ? dayjs(values.start_date).format("YYYY-MM-DD")
              : null,
            end_date: values.end_date
              ? dayjs(values.end_date).format("YYYY-MM-DD")
              : null,
            expiry: values.expiry
              ? dayjs(values.expiry).format("YYYY-MM-DD")
              : null,
            standard_deduction: values.standard_deduction,
            trades: values.trades?.length
              ? values.trades.map(trade => {
                  return {
                    id: trade.id as any,
                    buyer_id: trade.buyer?.shared_id,
                    attachments: trade.attachments.map(att => att.media.id),
                    items: (() => {
                      const itemInputs = trade.commodity_items?.length
                        ? buildReceiptTradeItemInputsFromBlocks(
                            trade.commodity_items,
                            { defaultCurrency }
                          )
                        : []
                      if (itemInputs.length) return itemInputs
                      return trade.items?.length
                        ? trade.items.map(item => {
                            return {
                              id: item.id as any,
                              quantity: Number(item.quantity),
                              parent_trade_item_id:
                                item.parent_trade_item_id as any,
                              rate: Number(item.rate),
                              unit_id:
                                (item as any).quantity_unit?.shared_id ||
                                values.unit?.shared_id,
                              currency_id: defaultCurrency?.id,
                              variety_grade_id: item.grade?.shared_id,
                            }
                          })
                        : []
                    })(),
                  }
                })
              : [],
            expenses: values?.expenses?.filter(i => i.seller || i.buyer)?.length
              ? values.expenses.reduce<any[]>((prev, expense) => {
                  const commonProperties = {
                    expense_parameter_id: expense.expense_parameter?.id,
                    kind: expense.kind?.id,
                  }
                  if (expense.buyer?.value) {
                    prev.push({
                      ...commonProperties,
                      id: expense.buyer.id,
                      paid_by: "buyer",
                      value: expense.buyer.value,
                    })
                  }
                  if (expense.seller?.value) {
                    prev.push({
                      ...commonProperties,
                      id: expense.seller.id,
                      paid_by: "seller",
                      value: expense.seller.value,
                    })
                  }
                  return prev
                }, [])
              : undefined,

            advance_payments:
              canAddAdvancePayment(values?.kind) &&
              values?.advance_payment?.length > 0
                ? values?.advance_payment.map(advance => ({
                    id: advance.id,
                    parameter_id: advance.parameter?.id,
                    amount: advance.amount,
                    bourn_by: advance.bourn_by?.id,
                  }))
                : [],

            credit_notes:
              values?.credit_notes?.length > 0 &&
              values?.credit_notes[0]?.amount > 0
                ? values?.credit_notes.map(credit => ({
                    id: credit.id,
                    amount: credit.amount,
                    remarks: credit.remarks,
                  }))
                : [],

            supplier_expense_gst_auto_calculated:
              values.expense_supplier_gst_auto_calculated ?? true,

            seller_gst_attachments: values.seller_gst_attachments?.length
              ? values.seller_gst_attachments.map((att: any) => att.media?.id)
              : undefined,

            expense_taxes: (() => {
              const taxRows: {
                tax_name: string
                percentage: number
              }[] = []
              if (
                (values.expense_supplier_sgst_percentage ?? 0) > 0 ||
                (values.expense_supplier_sgst ?? 0) > 0
              ) {
                taxRows.push({
                  tax_name: "SGST",
                  percentage: values.expense_supplier_sgst_percentage!,
                })
              }
              if (
                (values.expense_supplier_cgst_percentage ?? 0) > 0 ||
                (values.expense_supplier_cgst ?? 0) > 0
              ) {
                taxRows.push({
                  tax_name: "CGST",
                  percentage: values.expense_supplier_cgst_percentage!,
                })
              }
              if (
                (values.expense_supplier_igst_percentage ?? 0) > 0 ||
                (values.expense_supplier_igst ?? 0) > 0
              ) {
                taxRows.push({
                  tax_name: "IGST",
                  percentage: values.expense_supplier_igst_percentage!,
                })
              }
              return taxRows.length > 0 ? taxRows : undefined
            })(),
          },
        })
        if (data?.add_update_receipt?.receipt.id) {
          toast.custom(<Toast title="Receipt saved successfully" />)
          setOpen(false)
          setTimeout(() => {
            if (pData?.packaging_order.length || 0 > 0) {
              setPackingOpen(true)
            } else {
              navigate(AppRoutes.tradeReceipts)
            }
          }, 500)
        }

        if (error) {
          throw new Error(error.message)
        }
      } catch (error: any) {
        return toast.custom(<Toast title={error?.message} intent="danger" />)
      }
    })
  }

  const finalTotal =
    (form.values.total_amount || 0) - (form.values.amount_deducted || 0)

  const isValidReceipt = true

  return (
    <>
      <header className="bg-gray-100 shadow-sm p-3 border-b-gray-300 border-b flex justify-between">
        <SpinnerOverlay show={fetching} />
        <Breadcrumbs
          items={[
            {
              text: "Receipts",
              link: AppRoutes.tradeReceipts,
              isActive: false,
            },
            {
              text: id?.toString() || "New",
              link: AppRoutes.tradeReceiptDetail(id || "new"),
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
      </header>
      <Group className="flex flex-col md:flex-row gap-4 px-2 md:px-5 pt-4 md:pt-8 pb-24 md:pb-16 relative">
        <div
          className={classNames(
            "w-full md:flex-[0_0_300px] md:self-start",
            !form.values.parent_receipt_id &&
              form.values.child_receipts?.length === 0 &&
              "hidden"
          )}
        >
          <Card
            className={classNames(
              "border rounded-lg overflow-hidden self-start",
              !form.values.parent_receipt_id &&
                form.values.child_receipts?.length === 0 &&
                "hidden"
            )}
          >
            {form.values.parent_receipt_id && (
              <Link
                to={
                  AppRoutes?.tradeReceipts + "/" + form.values.parent_receipt_id
                }
                className="flex-1 block"
              >
                <SideBarItem active={false}>
                  Parent Receipt - # {form.values.parent_receipt_id}
                </SideBarItem>
              </Link>
            )}
            {form.values.child_receipts?.length > 0 &&
              form.values.child_receipts.map((child, index) => (
                <Link
                  to={AppRoutes?.tradeReceipts + "/" + child.id}
                  className="flex-1 block"
                >
                  <SideBarItem active={false}>
                    Child Receipt - # {child.id}
                  </SideBarItem>
                </Link>
              ))}
          </Card>
        </div>

        <div className="w-full md:w-[calc(100%-316px)] lg:w-[70%] md:mx-0 lg:mx-auto min-w-0">
          <WhoIsTrading
            form={form}
            inputProps={inputProps}
            isDisabled={isDisabled || isHishobPattiAdded}
            showBuyer={false}
            showAllowMultipleCommodity={canAddMultiCommodity}
          />
          <HowIsItTraded
            form={form}
            showBookingKinds
            inputProps={inputProps}
            isDisabled={isDisabled || isHishobPattiAdded}
            hideMaterialKind
          />
          <WhoOverseesTrading
            inputProps={inputProps}
            isDisabled={isDisabled || isHishobPattiAdded}
          />
          <WhatIsBeingTraded
            form={form}
            inputProps={inputProps}
            isDisabled={isDisabled || isHishobPattiAdded}
            isMulti
          />
          <FormCard title="When is it traded">
            <div
              className={"grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"}
            >
              <FormDateInput
                {...inputProps("start_date")}
                label="Start Date of Trade"
                required
              />
              <FormGroup label="End Date of Trade">
                <DateInput {...inputProps("end_date")} />
              </FormGroup>
              <FormGroup label="Validity of Trade" required>
                <DateInput {...inputProps("expiry")} />
              </FormGroup>
              <FormGroup label="Vehicle">
                <QuerySelector
                  dataHook={useAllVehiclesQuery}
                  serverSideQuery
                  useWildCardsInQuery={false}
                  variables={{
                    first: 20,
                  }}
                  getOptionLabel={item => item.codename}
                  {...inputProps("vehicle", {
                    group: SharedEntityGroup.StarlingVehicle,
                  })}
                />
              </FormGroup>
            </div>
          </FormCard>
          <FormCard
            childrenClassName={"!p-0"}
            title="Trade Particulars"
            action={
              <Button
                disabled={isDisabled || isHishobPattiAdded}
                onClick={() => form.insertListItem("trades", TRADE)}
                leftIcon={<Plus />}
                size="sm"
              >
                Buyer
              </Button>
            }
          >
            <ReceiptParticulars
              form={form}
              inputProps={inputProps}
              runComputations={runComputations}
              isDisabled={isDisabled || isHishobPattiAdded}
              defaultCurrency={defaultCurrency}
              useCredit={useCredit}
              allowMultipleCommodity={Boolean(state.allow_multiple_commodity)}
              supplierSameState={supplierSameState}
              sharedDeptId={sharedDeptId}
            />
          </FormCard>
          <TradeExpenses
            form={form}
            inputProps={inputProps}
            isDisabled={isDisabled || isHishobPattiAdded}
            advancePaymentDisabled={!canAddAdvancePayment(form.values?.kind)}
            sharedDeptId={sharedDeptId}
            isReceipt={true}
            showOverallExpenseGst={true}
            supplierSameState={(() => {
              const deptCode = departmentData?.address?.state_region?.code
              const sellerCode =
                state?.seller?.current_address?.state_region?.code
              if (!deptCode || !sellerCode) return undefined
              return deptCode === sellerCode
            })()}
            onExpenseGstFieldChange={(field, value) => {
              const updatedValues = { ...form.values, [field]: value }
              setTimeout(() => runComputations(updatedValues), 0)
            }}
            onExpenseTemplateChange={expenseConfig => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.expenses = normalizeExpenses(
                expenseConfig.expense_configs || []
              )
              runComputations(stateCopy)
              form.setValues(stateCopy)
            }}
            onDeleteExpense={idx => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.expenses.splice(idx, 1)
              runComputations(stateCopy)
              form.setValues(stateCopy)
            }}
            onDeleteAdvancePayment={idx => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.advance_payment.splice(idx, 1)
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
          />
          {(() => {
            const hasOrgConfig =
              (form.values.commodity_variety
                ?.organisation_commodity_variety_configs?.[0]?.taxes?.length ??
                0) > 0
            if (!hasOrgConfig) return null

            const sellerMissingState =
              form.values.seller?.id &&
              !form.values.seller?.current_address?.state_region
            const buyerMissingState = form.values.trades?.some(
              trade =>
                trade.buyer?.id && !trade.buyer?.current_address?.state_region
            )

            if (!sellerMissingState && !buyerMissingState) return null

            return (
              <div className="mb-4 flex flex-col gap-2">
                {sellerMissingState && (
                  <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-md text-sm flex items-center gap-2 font-500">
                    <span>⚠</span> Supplier is missing a state region code. GST
                    cannot be calculated.
                  </div>
                )}
                {buyerMissingState && (
                  <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-md text-sm flex items-center gap-2 font-500">
                    <span>⚠</span> One or more buyers are missing a state region
                    code. GST cannot be calculated.
                  </div>
                )}
              </div>
            )
          })()}
          <FormCard
            childrenClassName="flex flex-col gap-0 text-sm pb-2"
            title="Supplier Payment"
          >
            <PaymentRow
              label={
                <span className="text-gray-700">
                  Final Total{" "}
                  <span className="text-xs text-gray-600 italic">
                    (Excl. Expense GST)
                  </span>
                </span>
              }
              value={
                <span className="text-gray-900 font-600">
                  {inr(finalTotal)}
                </span>
              }
            />

            <PaymentRow
              label={
                <span className="text-gray-700">
                  Supplier Expense{" "}
                  <span className="text-xs text-gray-600 italic">
                    (incl. Expense GST)
                  </span>
                </span>
              }
              value={
                <span className="font-600 text-red-400">
                  {inr(
                    (form.values.seller_expense_amount || 0) +
                      totalExpenseSupplierGst
                  )}
                </span>
              }
            />

            <PaymentRow
              label="Supplier Charge"
              value={
                <span className="font-600 text-red">
                  {inr(form.values.supplier_charge || 0)}
                </span>
              }
            />

            {form.values?.credit_notes[0]?.amount ? (
              <PaymentRow
                label="Credit Note"
                value={
                  <span className="font-600">
                    {inr(form.values?.credit_notes[0]?.amount)}
                  </span>
                }
              />
            ) : null}

            <PaymentRow
              label="Supplier GST"
              value={<span className="font-600">{inr(totalSupplierGst)}</span>}
            />

            {settledByAdvanceAmount ? (
              <PaymentRow
                label={
                  <div className="flex gap-1 items-center ">
                    Supplier Advance Utilized{" "}
                    <Tooltip
                      maxWidth="none"
                      content={
                        <div className="min-w-max whitespace-nowrap">
                          {settledByAdvance?.map((adv, index) => {
                            return (
                              <div
                                key={adv?.id ?? index}
                                className="flex items-center justify-between gap-6"
                              >
                                <span>
                                  {`${
                                    TradeKindsReadable[
                                      adv?.parent_transaction?.advance_payment
                                        ?.receipt?.kind as any
                                    ]
                                  } #${
                                    adv?.parent_transaction?.advance_payment
                                      ?.receipt?.id
                                  } | Adv id #${
                                    adv?.parent_transaction?.advance_payment?.id
                                  }`}
                                </span>
                                <span className="font-700">
                                  {inr(adv?.amount || 0)}
                                </span>
                              </div>
                            )
                          })}
                          <MenuDivider className="border-blue-600" />
                          <div className="flex items-center justify-between gap-6">
                            <span>Total Amount Used:</span>
                            <span className="font-700">
                              {inr(settledByAdvanceAmount || 0)}
                            </span>
                          </div>
                        </div>
                      }
                    >
                      <Info
                        className="text-[#0677F2] cursor-pointer"
                        weight="fill"
                        size={20}
                      />
                    </Tooltip>
                  </div>
                }
                value={
                  <span className="text-gray-900 font-600">
                    {inr(settledByAdvanceAmount || 0)}
                  </span>
                }
              />
            ) : null}
            <div className="flex justify-between items-center py-2 px-4 border-t border-dashed mt-2">
              <span className="font-500">
                <span className="text-green-500 text-sm">
                  Seller Payable Amount
                </span>{" "}
                <span className="text-xs text-gray-600 italic ml-1">
                  (i.e. Final Total + Credit Note - Seller Expense - Expense GST
                  - Advance payment - Supplier Charge + GST)
                </span>
              </span>
              <div className="flex items-center gap-4">
                <span className="font-600 text-gray-900">
                  {inr(form.values?.seller_receivable_amount || 0)}
                </span>
                <div className="w-[40px]"></div>
              </div>
            </div>
          </FormCard>
          <TermsAndConditions
            isDisabled={isDisabled || isHishobPattiAdded}
            kind={form.values.kind}
            mode={form.values.mode}
            sharedDeptId={sharedDeptId}
            seller_tnc={form.values.seller_tnc}
            buyer_tnc={form.values.buyer_tnc}
            onChange={(path, value) => form.setFieldValue(path, value)}
          />
        </div>
      </Group>
      <footer className="fixed bg-white left-0 bottom-0 w-full px-3 py-2 shadow-sm flex flex-wrap justify-end gap-2 z-10">
        <Button
          onClick={() => {
            const validation = form.validate()
            if (validation.hasErrors) {
              return toast.custom(
                <Toast
                  title="Please fix validation errors"
                  intent="danger"
                  subtitle={Object.values(validation.errors)?.[0]}
                />
              )
            }
            setOpen(true)
          }}
          loading={mutationFetching}
          leftIcon={<CheckCircle />}
          disabled={!isValidReceipt}
        >
          {isDisabled || isHishobPattiAdded
            ? "View Summary"
            : `${form.values.id ? "Save" : "Create"} ${
                form.values.kind === "final"
                  ? "Hishob Patti"
                  : form.values.kind === "booking"
                  ? "Transit Patti"
                  : "Sauda Pavti"
              }`}
        </Button>
      </footer>
      <CreateReceiptSummary
        isLoading={mutationFetching}
        receipt={form.values}
        primaryActionButtonProps={{
          text: "Save",
          disabled: isDisabled || isHishobPattiAdded,
          leftIcon: <CheckCircle />,
          onClick:
            isDisabled || isHishobPattiAdded
              ? undefined
              : (handleSubmit() as any),
          loading: mutationFetching,
        }}
        isOpen={isOpen}
        onClose={async () => {
          setOpen(false)
        }}
      />
      <PackingMaterialSaveModal
        pData={pData}
        isOpen={packingOpen}
        onClose={() => {
          setPackingOpen(false)
          navigate(AppRoutes.tradeReceipts)
        }}
      />
    </>
  )
}

export default ReceiptDetail
